import { getLocale, type Locale } from '@/lib/locale';

export const GROK_IMAGE_TO_VIDEO_PATH = '/image-to-video';

export const GROK_IMAGE_TO_VIDEO_SEO_TITLE =
  'Free Grok Image to Video | Single & Multi-Image Generator';

export const GROK_IMAGE_TO_VIDEO_SEO_DESCRIPTION =
  'Use the free-to-start Grok Image to Video generator with one frame or multiple references. Optimize motion, generate a clip, then edit, extend, or upscale.';

export const GROK_IMAGE_TO_VIDEO_DATE_PUBLISHED = '2026-06-30';

export const GROK_IMAGE_TO_VIDEO_LAST_UPDATED = '2026-09-16';

export const GROK_IMAGE_TO_VIDEO_LAST_UPDATED_LABEL = 'September 16, 2026';

const GROK_IMAGE_TO_VIDEO_MEDIA_UPLOAD_DATE = '2026-07-09T00:00:00+00:00';

export const GROK_IMAGE_TO_VIDEO_KEYWORDS = [
  'grok image to video',
  'grok ai image to video',
  'how to use grok image to video',
  'grok image to video prompts',
  'animate images with grok',
  'grok image to video free online',
  'grok image to video generator',
  'grok imagine image to video',
].join(', ');

export const GROK_IMAGE_TO_VIDEO_QUICK_ANSWER =
  'Grok Image to Video is a free-to-start workflow that turns one start frame or role-labeled references into an AI clip. Use Grok Image to Video for 1-15 second single-image clips at 480p or 720p, or choose a compatible multi-image model to combine people, products, and scenes. Generation uses credits, and results can be edited, extended, upscaled, restyled, downloaded, or reopened from History.';

export const GROK_IMAGE_TO_VIDEO_FEATURES = [
  {
    title: 'Single-image and role-aware multi-image inputs',
    body: 'Grok Image to Video can start from one prepared frame or from role-labeled people, products, and scene references. Multi-image templates keep every upload tied to a clear job before generation.',
  },
  {
    title: 'Seven-section realistic motion prompts',
    body: 'The Grok Image to Video optimizer builds MAIN SUBJECT, LOCATION, VISUAL STYLE, CAMERA STYLE, TIMELINE, AUDIO, and TARGET. It adds device flaws, visible atmosphere, non-perfect events, and consistency checks.',
  },
  {
    title: 'Model-aware reference matching',
    body: 'Grok Image to Video matches the number and purpose of your references to compatible models. The workbench shows reference limits, duration, resolution, and audio support before credits are used.',
  },
  {
    title: 'Result actions for the next production step',
    body: 'Every Grok Image to Video result can continue to source editing, extension, upscaling, restyling, the video editor, download, or History instead of ending at the first draft.',
  },
];

export const GROK_IMAGE_TO_VIDEO_STEPS = [
  {
    name: 'Prepare one image or assign multiple roles',
    text: 'Start Grok Image to Video with one strong frame, or assign people, products, scenes, and custom roles in multi-image mode. Select a model that accepts every attached file.',
  },
  {
    name: 'Build one clear motion direction',
    text: 'The Grok Image to Video prompt optimizer expands the action into subject locks, location, visual and camera behavior, timing, audio, and a realistic target.',
  },
  {
    name: 'Generate, review, and continue from the result',
    text: 'Review the Grok Image to Video result for identity, object shape, geography, motion, and sound. Then edit the source, extend, upscale, restyle, download, or revisit History.',
  },
];

export const GROK_IMAGE_TO_VIDEO_SUPPORTED_SETTINGS = [
  {
    setting: 'Reference setup',
    options:
      'Single image; Two people; Product + scene; Person + product; Custom',
    bestFor:
      'A start frame, identity pairing, object placement, or a composed multi-reference scene.',
  },
  {
    setting: 'Model matching',
    options:
      'Grok Imagine 1.5: 1 image; Grok Imagine: up to 7; Seedance 2.0 / Fast and MiniMax H3: up to 9',
    bestFor:
      'Choosing a compatible model before uploading several people, objects, or scene references.',
  },
  {
    setting: 'Aspect ratio',
    options: '2:3, 3:2, 1:1, 9:16, or 16:9',
    bestFor:
      'Vertical Shorts and Reels, square feed posts, wide previews, and ecommerce media.',
  },
  {
    setting: 'Duration',
    options: '1 to 15 seconds with Grok Imagine 1.5',
    bestFor:
      'Fast motion tests, social drafts, looping visuals, and 15-second concept previews.',
  },
  {
    setting: 'Resolution',
    options: '480p or 720p',
    bestFor: 'Quick low-cost drafts at 480p or sharper review clips at 720p.',
  },
];

export const GROK_IMAGE_TO_VIDEO_REAL_EXAMPLES = [
  {
    id: 'product-motion-demo',
    title: 'Product photo to motion ad',
    inputLabel: 'Input image',
    inputImage: '/video-examples/showcase-product.webp',
    inputAlt:
      'Clean product image used as the input for an image to video example',
    setting: '16:9 · 6s · 720p',
    prompt:
      'Slow camera push-in, soft studio light sweep, clean product reveal, premium commercial mood.',
    outputLabel: 'Output video',
    outputVideo: '/video-examples/demo-image-to-video-veo-3-1.mp4',
    outputPoster: '/video-examples/demo-image-to-video-veo-3-1-poster.jpg',
    duration: 'PT6S',
    width: 1280,
    height: 720,
    uploadDate: GROK_IMAGE_TO_VIDEO_MEDIA_UPLOAD_DATE,
    description:
      'A product source image becomes a short commercial-style motion draft with a smooth camera move and studio lighting.',
  },
  {
    id: 'character-motion-demo',
    title: 'Character image to social motion',
    inputLabel: 'Input image',
    inputImage: '/video-examples/motion-control-sample-character.jpg',
    inputAlt:
      'Character image used as the input for an image to video motion example',
    setting: '9:16 · 6s · 720p',
    prompt:
      'MAIN SUBJECT: Keep the same face, hair, clothing, proportions, and appearance throughout.\nLOCATION: Late-afternoon garden path with moving leaf shadows, damp stones, distant trees, and intermittent breeze.\nVISUAL STYLE: Natural portrait realism, unposed body language, visible wind response, soft skin texture.\nCAMERA STYLE: Friend-recorded phone footage with micro-shake, focus breathing, motion blur, an uneven reframe, no stabilization, no cinematic dolly.\nTIMELINE: 00:00-00:02 she notices the camera as two hair strands lift; 00:02-00:04 she starts a hand gesture and glances away; 00:04-00:06 she finishes as focus catches up.\nAUDIO: Leaves, distant voices, fabric movement, birds; no music or narration.\nTARGET: A spontaneous social clip recorded by a friend on a phone.',
    outputLabel: 'Output video',
    outputVideo: '/video-examples/motion-control-sample-result-video-100c.mp4',
    outputPoster:
      '/video-examples/motion-control-sample-result-poster-100c.jpg',
    duration: 'PT6S',
    width: 944,
    height: 1280,
    uploadDate: GROK_IMAGE_TO_VIDEO_MEDIA_UPLOAD_DATE,
    description:
      'A character source image becomes a vertical social clip while keeping the character visually consistent.',
  },
  {
    id: 'creator-dance-demo',
    title: 'Creator image to dance clip',
    inputLabel: 'Input image',
    inputImage: '/video-examples/tool-dance.webp',
    inputAlt: 'Creator image used as the input for a dance video example',
    setting: '4:3 · 6s · 720p',
    prompt:
      'Animate the subject with rhythmic movement, stable face detail, bright creator lighting, and energetic short-form pacing.',
    outputLabel: 'Output video',
    outputVideo: '/video-examples/example-dance.webm',
    outputPoster: '/video-examples/example-dance-poster.jpg',
    duration: 'PT6S',
    width: 2566,
    height: 1924,
    uploadDate: GROK_IMAGE_TO_VIDEO_MEDIA_UPLOAD_DATE,
    description:
      'A creator-style source image becomes a short dance motion example for social video testing.',
  },
];

export const GROK_IMAGE_TO_VIDEO_BEST_INPUTS = [
  {
    title: 'Clear catalog photos',
    body: 'Use a centered item with visible edges, enough light, and minimal background clutter. This gives the generator a stable subject to animate.',
    prompt:
      'Lock package shape and label; use a handheld camera with slight focus breathing, one natural light sweep, and no sterile 3D rendering.',
  },
  {
    title: 'Portraits or characters with one main subject',
    body: 'Choose a face or character pose with a clear silhouette. Ask for one controlled action such as a head turn, hair movement, or a subtle expression change.',
    prompt:
      'Lock identity and clothing; add one head turn, wind response, a late handheld reframe, and one brief autofocus search.',
  },
  {
    title: 'Scene concepts with a simple motion path',
    body: 'Landscapes, rooms, vehicles, and campaign frames work best when the motion prompt names one camera move and one environmental change.',
    prompt:
      'Define the travel direction, camera source, environmental response, natural location sound, and one imperfect event per shot.',
  },
];

export const GROK_IMAGE_TO_VIDEO_EXAMPLES = [
  {
    title: 'Handheld package reveal',
    input: 'A clean package reference plus a bathroom scene reference.',
    prompt:
      'MAIN SUBJECT: The blue skincare tube stays label-forward and dimensionally consistent.\nLOCATION: A warm vanity with practical mirror bulbs, cosmetics, pale tile, and soft counter reflections.\nVISUAL STYLE: Tactile commercial realism, humid air visible as faint mirror bloom.\nCAMERA STYLE: Handheld phone footage with focus breathing, slight exposure correction, rolling shutter, imperfect framing, no gimbal.\nTIMELINE: 00:00-00:02 late handheld reveal; 00:02-00:04 the tube settles as the focus searches once; 00:04-00:06 a natural light sweep crosses the label.\nAUDIO: Room tone, soft packaging contact, distant ventilation; no music.\nTARGET: A believable creator-shot package insert, not a sterile 3D render.',
    setting: 'Product + scene, 16:9, 6 seconds, 720p',
    result:
      'An ad draft that carries the package identity into a specific environment.',
  },
  {
    title: 'Character portrait with imperfect motion',
    input: 'A portrait or AI character image with a clear face and silhouette.',
    prompt:
      'MAIN SUBJECT: The character keeps the same face, hair, clothing, and proportions.\nLOCATION: Late-afternoon garden path with leaf shadows, damp stones, distant trees, and intermittent breeze.\nVISUAL STYLE: Natural portrait realism with visible wind response and unposed body language.\nCAMERA STYLE: Friend-recorded phone video with micro-shake, focus breathing, slight motion blur, uneven reframe, no stabilization.\nTIMELINE: 00:00-00:02 she notices the camera as wind lifts two hair strands; 00:02-00:04 she starts a hand gesture and briefly looks away; 00:04-00:06 she finishes the gesture as focus catches up.\nAUDIO: Leaves, distant voices, fabric movement, birds; no score.\nTARGET: A spontaneous social clip recorded by a friend on a phone.',
    setting: '1:1, 6 seconds, 720p',
    result:
      'A recognizable character motion draft with deliberate human and camera imperfections.',
  },
  {
    title: 'Coastal multi-reference sequence',
    input: 'Two people, a mint scooter, and a coastal road reference.',
    prompt:
      'MAIN SUBJECT: The same man drives while the same woman remains the passenger; both identities, outfits, height relationship, and scooter design stay locked.\nLOCATION: Bright coastal road, downhill curve, railway crossing, stone walls, orange mirror, utility wires, white-capped sea.\nVISUAL STYLE: Faded 2000s Japanese film print with milky blacks, halation, grain, and washed pastel color.\nCAMERA STYLE: Roadside and tracking handheld footage with gate weave, exposure flicker, shoulder sway, late reframes, no stabilization.\nTIMELINE: Five connected shots move from roadside wide to tracking faces, hands insert, low wheel detail, then mirror wide; every shot adds one distinct imperfection and preserves screen direction.\nAUDIO: Scooter engine, wind, waves, gulls, brief laughter, optical hiss; no music.\nTARGET: A found summer-memory reel with consistent people, scooter, geography, and travel direction.',
    setting: 'Custom 4 references, 16:9, 12-15 seconds, 720p',
    result:
      'A continuity-focused multi-shot draft with locked identities, vehicle, location, and screen direction.',
  },
];

export const GROK_IMAGE_TO_VIDEO_USE_CASES = [
  {
    title: 'Turn product images into short promo clips',
    body: 'Use Grok Image to Video with a product photo, or combine the item with a separate scene reference. Add a slow camera move or light change for ads, landing pages, social posts, and ecommerce tests.',
  },
  {
    title: 'Animate characters, portraits, or AI artwork',
    body: 'Grok Image to Video can animate a character pose, portrait, poster, or concept image while the prompt guides hair movement, facial direction, atmosphere, and camera behavior.',
  },
  {
    title: 'Create social video drafts from visual concepts',
    body: 'Animate Images With Grok by starting from a thumbnail, scene mockup, travel image, or campaign visual. Grok Image to Video creates short motion drafts for TikTok, Reels, Shorts, or X before the final edit.',
  },
];

export const GROK_IMAGE_TO_VIDEO_VALUE_ROWS = [
  {
    need: 'Keep the original visual',
    value: 'Start from an uploaded image instead of a blank prompt.',
  },
  {
    need: 'Control motion direction',
    value: 'Describe subject movement, camera behavior, and atmosphere.',
  },
  {
    need: 'Create social-ready drafts',
    value: 'Choose vertical, square, or wide formats for common channels.',
  },
  {
    need: 'Iterate quickly',
    value:
      'Review the generated clip and refine the prompt in the same workspace.',
  },
];

export const GROK_IMAGE_TO_VIDEO_LIMITS = [
  'Use rights-cleared images in Grok Image to Video, and upload private portraits or brand assets only with permission.',
  'Single-image and multi-image support depends on the selected model; use the Grok Image to Video model table to check current reference limits.',
  'Identity, package details, tiny text, complex hands, and crowded scenes require review.',
  'Audio and 4K output are model-dependent; Grok Imagine 1.5 supports 480p and 720p without generated audio.',
  'Opening the workspace is free, but video generation uses the credit cost shown before you generate.',
  'Generated clips should be reviewed before ads, client work, ecommerce pages, or public social posts.',
];

export const GROK_IMAGE_TO_VIDEO_FAQS = [
  {
    question: 'What is Grok Image to Video?',
    answer:
      'Grok Image to Video uses one start frame or role-labeled references to create an AI clip. Images establish people, products, or a scene; the motion prompt controls action, camera, timing, audio, and continuity. If you still need a still frame, start on the Grok image generator first.',
  },
  {
    question: 'Should I use single-image or multi-image mode?',
    answer:
      'Use Grok Image to Video single-image mode when one frame already contains the scene. Choose multi-image mode when people, products, and locations come from separate files and need explicit roles.',
  },
  {
    question: 'Which models support multiple image references?',
    answer:
      'Grok Image to Video supports different reference limits by model. The standard Grok model accepts 7 references; Seedance 2.0, Seedance Fast, and MiniMax H3 accept 9, while version 1.5 accepts one image and supports 1-15 seconds at 480p or 720p.',
  },
  {
    question: 'How does the realistic video prompt optimizer work?',
    answer:
      'The Grok Image to Video optimizer expands a direction into MAIN SUBJECT, LOCATION, VISUAL STYLE, CAMERA STYLE, TIMELINE, AUDIO, and TARGET. This Grok AI image to video process adds device flaws, visible atmosphere, non-perfect events, and consistency checks.',
  },
  {
    question: 'What can I do after a video is generated?',
    answer:
      'A Grok Image to Video result can continue to source editing, extend, upscale, restyle, the video editor, download, or History. If official Grok paused, read the Grok Imagine limit FAQ before retrying. Credit availability is shown in the workbench before generation.',
  },
];

export const GROK_IMAGE_TO_VIDEO_SCHEMA_FEATURES = [
  'Single-image and role-aware multi-image video generation',
  'Two people, Product + scene, Person + product, and custom templates',
  'Seven-section realistic video prompt optimization',
  'Camera imperfections, observable atmosphere, non-perfect events, and consistency checks',
  'Aspect ratio controls for 2:3, 3:2, 1:1, 9:16, and 16:9 outputs',
  'Duration controls for 1 to 15-second Grok Imagine 1.5 clips',
  '480p and 720p resolution options',
  'Edit source image, extend, upscale, restyle, edit, and download actions',
  'Session preview, History, and video download workflow',
];

const EN_GROK_IMAGE_TO_VIDEO_DATA = {
  path: GROK_IMAGE_TO_VIDEO_PATH,
  seoTitle: GROK_IMAGE_TO_VIDEO_SEO_TITLE,
  seoDescription: GROK_IMAGE_TO_VIDEO_SEO_DESCRIPTION,
  datePublished: GROK_IMAGE_TO_VIDEO_DATE_PUBLISHED,
  lastUpdated: GROK_IMAGE_TO_VIDEO_LAST_UPDATED,
  lastUpdatedLabel: GROK_IMAGE_TO_VIDEO_LAST_UPDATED_LABEL,
  keywords: GROK_IMAGE_TO_VIDEO_KEYWORDS,
  quickAnswer: GROK_IMAGE_TO_VIDEO_QUICK_ANSWER,
  features: GROK_IMAGE_TO_VIDEO_FEATURES,
  steps: GROK_IMAGE_TO_VIDEO_STEPS,
  settings: GROK_IMAGE_TO_VIDEO_SUPPORTED_SETTINGS,
  realExamples: GROK_IMAGE_TO_VIDEO_REAL_EXAMPLES,
  bestInputs: GROK_IMAGE_TO_VIDEO_BEST_INPUTS,
  examples: GROK_IMAGE_TO_VIDEO_EXAMPLES,
  useCases: GROK_IMAGE_TO_VIDEO_USE_CASES,
  valueRows: GROK_IMAGE_TO_VIDEO_VALUE_ROWS,
  limits: GROK_IMAGE_TO_VIDEO_LIMITS,
  faqs: GROK_IMAGE_TO_VIDEO_FAQS,
  schemaFeatures: GROK_IMAGE_TO_VIDEO_SCHEMA_FEATURES,
  copy: {
    cover: {
      videoTab: 'Video',
      imageTab: 'Image',
      workflowBadge: 'Grok Imagine workflow',
      mode: 'Image to video',
      title: 'Create a 15 second Grok Imagine video from an image.',
      description:
        'Upload a clean start frame, describe the motion, and prepare a Grok AI video draft up to 15 seconds before you generate.',
      metrics: [
        { label: 'Start', value: 'Free' },
        { label: 'Duration', value: 'Up to 15s' },
        { label: 'Quality', value: '720p' },
      ],
      stats: [
        { title: 'Workflow', value: 'Free to start' },
        { title: 'Prompt focus', value: 'Motion + camera' },
        { title: 'Updated', value: GROK_IMAGE_TO_VIDEO_LAST_UPDATED_LABEL },
      ],
      uploadTitle: 'Start Frame',
      uploadFormat: 'JPG / PNG / WebP',
      motionPath: 'Motion path',
      motionSteps: [
        { title: 'Upload frame', text: 'Use a clear subject.' },
        { title: 'Prompt motion', text: 'Name the move.' },
        { title: 'Generate video', text: 'Review the clip.' },
      ],
      uploadCta: 'Create 15s video',
    },
    realExamples: {
      eyebrow: 'Real examples',
      title: 'Grok Image to Video examples: single and multi-image results',
      description:
        'These Grok AI image to video examples compare three Grok Image to Video workflows: one prepared frame, an item with a separate scene, and a four-reference sequence. Each case shows the inputs, output settings, prompt controls, and generated video.',
      promptLabel: 'Prompt',
      sampleWorkflow: 'Sample workflow',
    },
    fifteenSecond: {
      eyebrow: 'Workflow',
      title: 'How to use Grok Image to Video in three steps',
      description:
        'A Grok Image to Video workflow moves from prepared inputs to a structured motion prompt, then carries the generated result into editing, extension, upscaling, restyling, download, or History.',
      steps: [
        {
          title: 'Upload one clear image',
          body: 'Use a JPG, PNG, or WebP start frame with one main subject. The image becomes the first frame of the 15 second Grok video.',
        },
        {
          title: 'Choose Grok Imagine 1.5 and 15s',
          body: 'Keep the Grok Imagine 1.5 image-to-video model active, set the duration to 15 seconds, then choose 480p for drafts or 720p for review clips.',
        },
        {
          title: 'Write one motion direction',
          body: 'Describe one subject action, one camera movement, and the mood. Focused prompts are more reliable than asking for many movements at once.',
        },
      ],
    },
    bestInputs: {
      eyebrow: 'Best inputs',
      title: 'Choose a strong first frame before animation',
      description:
        'Before starting Grok Image to Video, check exposure, composition, identity, object shape, and scene detail. A weak opening frame carries defects into motion; use the image editor first when the source is not yet video-ready.',
      promptLabel: 'Prompt angle',
    },
    features: {
      eyebrow: 'Core capabilities',
      title: 'Core Grok Image to Video capabilities',
      description:
        'Grok Image to Video combines single and multi-image inputs, model-aware reference roles, realistic prompt optimization, and result actions that support the next production step.',
    },
    steps: {
      eyebrow: 'How it works',
      title: 'How to use the Grok image to video generator',
      description:
        'The best image-to-video results come from a clear start frame and a prompt that describes what should happen next, not a prompt that repeats every detail already visible in the image.',
    },
    settings: {
      eyebrow: 'Choose a mode and model',
      title: 'Choose the right model and input mode',
      description:
        'Choose a Grok Image to Video model by reference count and creative job, then check input mode, aspect ratio, duration, resolution, and audio support before spending credits.',
      headers: {
        setting: 'Setting',
        options: 'Available options',
        bestFor: 'Best for',
      },
    },
    promptExamples: {
      eyebrow: 'Prompt examples',
      title: 'Write stronger motion prompts for image animation',
      description:
        'Grok Image to Video prompts work better when they separate subject, location, style, camera behavior, timeline, audio, and target while adding physical imperfections and continuity locks.',
      headers: {
        useCase: 'Use case',
        input: 'Input image',
        prompt: 'Motion prompt',
        setting: 'Best setting',
        result: 'Expected result',
      },
    },
    useCases: {
      eyebrow: 'Use cases',
      title: 'Grok Image to Video use cases',
      description:
        'Use Grok Image to Video when a product, character, or campaign concept already has a visual direction and needs a short motion draft for review, testing, or publishing.',
    },
    value: {
      eyebrow: 'Value',
      title: 'Why Animate Images With Grok?',
    },
    limits: {
      eyebrow: 'Quality control',
      title: 'Input limits and quality checks',
      description:
        'Grok Image to Video still depends on source quality, model support, and human review. Check rights, identity, object details, text, hands, audio, and resolution before public or paid use.',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Questions about image animation',
      description:
        'Direct Grok Image to Video answers about input modes, compatible models, realistic prompt optimization, generation costs, and available result actions.',
    },
    cta: {
      eyebrow: 'Ready to animate your image?',
      title: 'Animate Images With Grok from one frame or several references.',
      upload: 'Start Grok Image to Video',
      model: 'Grok Imagine Video 1.5',
      text: 'Start from text instead',
      source: 'Edit source image',
      history: 'View history',
      generator: 'Grok image generator',
      limit: 'Grok Imagine limit FAQ',
    },
    schema: {
      softwareName: 'Grok Image to Video Generator',
      howToName: 'How to use the Grok image to video generator',
      howToDescription:
        'Choose single or multi-image references, build a seven-section realistic motion prompt, select a compatible model, generate a clip, then edit, extend, upscale, restyle, or download the result.',
      breadcrumbHome: 'Home',
      breadcrumbPage: 'Grok Image to Video Generator',
    },
  },
};

type GrokImageToVideoSeoData = typeof EN_GROK_IMAGE_TO_VIDEO_DATA;

const ES_GROK_IMAGE_TO_VIDEO_DATA: GrokImageToVideoSeoData = {
  ...EN_GROK_IMAGE_TO_VIDEO_DATA,
  seoTitle: 'Grok image to video | Convierte imagen a video',
  seoDescription:
    'Convierte una imagen en video con Grok image to video. Sube JPG, PNG o WebP, escribe un prompt de movimiento y genera clips cortos.',
  lastUpdatedLabel: '23 de julio de 2026',
  keywords: [
    'grok image to video',
    'grok imagen a video',
    'grok imagen a video gratis',
    'grok imagine image to video',
    'grok convertir imagen a video',
    'grok ai imagen a video',
    'grok image to video generator',
  ].join(', '),
  quickAnswer:
    'Grok image to video convierte una imagen fija en un video corto usando tu imagen subida como primer frame y tu prompt como direccion de movimiento. Es util para creadores, marketers y equipos de diseno que ya tienen un visual y quieren animarlo rapido sin empezar desde un prompt en blanco.',
  features: [
    {
      title: 'Convierte una imagen fija con Grok Imagine',
      body: 'Sube una imagen JPG, PNG o WebP y usala como punto de partida visual. El generador mantiene sujeto, escena y composicion como ancla mientras anade movimiento desde tu prompt.',
    },
    {
      title: 'Escribe prompts de movimiento para camara y sujeto',
      body: 'Describe que debe moverse, como debe comportarse la camara y que ambiente debe crear el clip: push-in lento, giro de cabeza, viento, cambio de luz o movimiento de fondo.',
    },
    {
      title: 'Elige ajustes pensados para creadores',
      body: 'Selecciona relacion de aspecto, duracion y resolucion para el canal final. El modelo Grok Imagine 1.5 image-to-video admite clips de 1 a 15 segundos en 480p o 720p.',
    },
    {
      title: 'Genera, revisa y refina en un workspace',
      body: 'Previsualiza el clip, descarga el resultado o ajusta imagen y prompt para otra version. El flujo sirve para iterar, no solo para una generacion unica.',
    },
  ],
  steps: [
    {
      name: 'Sube un frame inicial claro',
      text: 'Elige una imagen JPG, PNG o WebP con derechos claros. Funcionan mejor fotos de producto, retratos, concepts de escena y artwork con un sujeto facil de identificar.',
    },
    {
      name: 'Describe el movimiento y los ajustes',
      text: 'Escribe que debe ocurrir despues y elige modelo, relacion de aspecto, duracion y resolucion para el canal donde usaras el clip.',
    },
    {
      name: 'Genera, revisa y refina',
      text: 'Genera el clip Grok image to video, comprueba si el sujeto sigue reconocible y descarga el resultado o simplifica el prompt para otra version.',
    },
  ],
  settings: [
    {
      setting: 'Imagen de entrada',
      options: 'JPG, PNG o WebP bajo 10MB',
      bestFor:
        'Foto de producto, retrato, concept de escena o artwork usado como primer frame.',
    },
    {
      setting: 'Relacion de aspecto',
      options: '2:3, 3:2, 1:1, 9:16 o 16:9',
      bestFor:
        'Shorts y Reels verticales, posts cuadrados, previews anchas y media de producto.',
    },
    {
      setting: 'Duracion',
      options: '1 a 15 segundos con Grok Imagine 1.5',
      bestFor:
        'Pruebas rapidas de movimiento, borradores sociales, loops y previews mas largos.',
    },
    {
      setting: 'Resolucion',
      options: '480p o 720p',
      bestFor: 'Borradores rapidos en 480p o clips mas nitidos en 720p.',
    },
  ],
  realExamples: EN_GROK_IMAGE_TO_VIDEO_DATA.realExamples.map((item) => ({
    ...item,
    inputLabel: 'Imagen de entrada',
    outputLabel: 'Video de salida',
  })),
  bestInputs: [
    {
      title: 'Fotos de producto claras',
      body: 'Usa una foto centrada, con bordes visibles, buena luz y poco ruido de fondo. Asi el generador Grok image to video mantiene un sujeto estable al animar.',
      prompt:
        'Push-in lento de camara, barrido suave de luz de estudio, revelado limpio del producto, ambiente comercial premium.',
    },
    {
      title: 'Retratos o personajes con un sujeto principal',
      body: 'Elige una cara o pose con silueta clara. Pide una accion controlada como giro de cabeza, movimiento de cabello o cambio sutil de expresion.',
      prompt:
        'Ligero giro de cabeza, cabello moviendose suavemente, brillo cinematografico de fondo, mantener identidad estable.',
    },
    {
      title: 'Conceptos de escena con movimiento simple',
      body: 'Paisajes, habitaciones, vehiculos y frames de campana funcionan mejor cuando el prompt nombra un movimiento de camara y un cambio ambiental.',
      prompt:
        'Dolly suave hacia adelante, nubes moviendose, cambio de luz calida, atmosfera cinematografica natural.',
    },
  ],
  examples: [
    {
      title: 'Clip promocional de producto',
      input: 'Una foto limpia del producto con la etiqueta al frente.',
      prompt:
        'Push-in lento de camara, barrido suave de luz de estudio, brillo sutil en etiqueta, ambiente comercial premium.',
      setting: '9:16, 6 segundos, 720p',
      result:
        'Un borrador corto para anuncios, paginas de producto o tests sociales.',
    },
    {
      title: 'Animacion de retrato o personaje',
      input: 'Un retrato o personaje IA con cara y silueta claras.',
      prompt:
        'Cabello moviendose suavemente con viento, ligero giro de cabeza, brillo cinematografico de fondo, expresion calmada.',
      setting: '1:1, 6 segundos, 720p',
      result:
        'Un concepto de movimiento que mantiene reconocible el retrato original.',
    },
    {
      title: 'Preview de viaje o escena',
      input: 'Paisaje, habitacion, vehiculo o frame de campana.',
      prompt:
        'Nubes moviendose, brillo en el agua, camara handheld suave, atmosfera calida de atardecer.',
      setting: '16:9, 10 segundos, 720p',
      result:
        'Un preview corto para Reels, storyboards, thumbnails o mood tests.',
    },
  ],
  useCases: [
    {
      title: 'Convierte imagenes de producto en clips promocionales',
      body: 'Sube una foto de producto y pide un movimiento lento de camara, cambio de luz o movimiento de fondo. Sirve para anuncios, landing pages, posts sociales y pruebas ecommerce.',
    },
    {
      title: 'Anima personajes, retratos o arte IA',
      body: 'Usa Grok AI imagen a video para anadir movimiento a una pose, retrato, poster o concept. Los prompts pueden dirigir cabello, mirada, atmosfera y camara.',
    },
    {
      title: 'Crea borradores sociales desde conceptos visuales',
      body: 'Empieza con un thumbnail, mockup de escena, imagen de viaje o visual de campana, luego genera un clip corto para TikTok, Reels, Shorts o X.',
    },
  ],
  valueRows: [
    {
      need: 'Mantener el visual original',
      value: 'Empieza desde una imagen subida en vez de un prompt vacio.',
    },
    {
      need: 'Controlar la direccion del movimiento',
      value: 'Describe movimiento del sujeto, camara y atmosfera.',
    },
    {
      need: 'Crear borradores sociales',
      value:
        'Elige formatos verticales, cuadrados o anchos para canales comunes.',
    },
    {
      need: 'Iterar rapido',
      value:
        'Revisa el clip generado y ajusta el prompt en el mismo workspace.',
    },
  ],
  limits: [
    'Usa imagenes fuente con derechos claros antes de generar clips publicos o comerciales.',
    'Grok image to video funciona mejor con un sujeto claro y un movimiento principal.',
    'Escenas saturadas, texto pequeno, manos complejas y prompts sobrecargados pueden reducir consistencia.',
    'Evita subir retratos privados, imagenes con copyright o assets de marca sin permiso.',
    'Revisa los clips antes de usarlos en anuncios, trabajos de cliente, paginas de producto o redes.',
  ],
  faqs: [
    {
      question: 'Puede Grok convertir una imagen en video?',
      answer:
        'Si. Un flujo Grok image to video puede usar tu imagen subida como primer frame y animarla con un prompt de movimiento, relacion de aspecto, duracion y ajustes de resolucion.',
    },
    {
      question: 'Grok Imagine admite image to video?',
      answer:
        'Si. Grok Imagine puede admitir flujos image-to-video donde un frame fijo se convierte en un borrador corto con movimiento. Esta pagina esta pensada para usuarios que ya tienen una imagen y quieren generar el siguiente paso en video.',
    },
    {
      question: 'Como convierto una imagen en video con Grok?',
      answer:
        'Sube una imagen JPG, PNG o WebP clara y describe el movimiento. Un buen prompt incluye sujeto, camara, luz y atmosfera.',
    },
    {
      question: 'Que formatos de imagen funcionan para Grok image to video?',
      answer:
        'Usa una imagen JPG, PNG o WebP con derechos claros y menos de 10MB. Las mejores entradas tienen un sujeto claro, bordes visibles y suficiente detalle para conservar el frame original.',
    },
    {
      question: 'Que prompt funciona mejor para Grok imagen a video?',
      answer:
        'Usa prompts especificos pero no sobrecargados: dolly-in lento, giro suave de cabeza, luz calida o movimiento de fondo relacionado.',
    },
    {
      question: 'Cuanto puede durar un clip Grok image to video?',
      answer:
        'El flujo predeterminado de Grok Imagine 1.5 image-to-video admite clips de 1 a 15 segundos. Empieza con un clip corto para probar el movimiento y sube a 15 segundos cuando el sujeto y la camara se vean estables.',
    },
  ],
  schemaFeatures: [
    'Generacion imagen a video desde JPG, PNG o WebP',
    'Prompts de movimiento para camara, sujeto y atmosfera',
    'Controles de aspecto 2:3, 3:2, 1:1, 9:16 y 16:9',
    'Controles de duracion de 1 a 15 segundos para Grok Imagine 1.5',
    'Opciones de resolucion 480p y 720p',
    'Preview de sesion y descarga de video',
  ],
  copy: {
    ...EN_GROK_IMAGE_TO_VIDEO_DATA.copy,
    cover: {
      videoTab: 'Video',
      imageTab: 'Imagen',
      workflowBadge: 'Flujo Grok Imagine',
      mode: 'Imagen a video',
      title: 'Convierte un frame fijo en un borrador corto de movimiento Grok.',
      description:
        'Sube un frame inicial limpio, describe el movimiento y genera un video corto.',
      metrics: [
        { label: 'Entrada', value: 'Frame' },
        { label: 'Duracion', value: 'Hasta 15s' },
        { label: 'Formato', value: '3 ratios' },
      ],
      stats: [
        { title: 'Flujo', value: 'Imagen a video' },
        { title: 'Prompt', value: 'Movimiento + camara' },
        { title: 'Actualizado', value: '9 jul 2026' },
      ],
      uploadTitle: 'Frame inicial',
      uploadFormat: 'JPG / PNG / WebP',
      motionPath: 'Ruta de movimiento',
      motionSteps: [
        { title: 'Sube frame', text: 'Usa una imagen con sujeto claro.' },
        {
          title: 'Prompt de movimiento',
          text: 'Nombra un movimiento principal.',
        },
        { title: 'Genera video', text: 'Revisa el clip.' },
      ],
      uploadCta: 'Subir imagen',
    },
    realExamples: {
      eyebrow: 'Ejemplos de video',
      title: 'Mira resultados imagen a video antes de la lista de funciones',
      description:
        'Cada ejemplo conecta imagen fuente, ajustes y video de salida para juzgar la calidad del movimiento.',
      promptLabel: 'Prompt',
      sampleWorkflow: 'Flujo de ejemplo',
    },
    bestInputs: {
      eyebrow: 'Mejores entradas',
      title: 'Mejores entradas para Grok image to video',
      description:
        'Antes de gastar creditos, revisa si la imagen fuente tiene sujeto claro, ruta de movimiento simple y suficiente detalle para conservar el primer frame reconocible.',
      promptLabel: 'Angulo del prompt',
    },
    features: {
      eyebrow: 'Funciones',
      title: 'Funciones clave del flujo Grok image to video',
      description:
        'Sube imagen, escribe direccion de movimiento y elige modelo, aspecto, duracion y resolucion antes de generar.',
    },
    steps: {
      eyebrow: 'Como funciona',
      title: 'Como usar el generador Grok image to video',
      description:
        'Un buen resultado nace de un frame claro y un prompt que explica lo que ocurre despues, no de repetir lo que ya se ve.',
    },
    settings: {
      eyebrow: 'Ajustes',
      title: 'Ajustes y requisitos de entrada',
      description:
        'Antes de gastar creditos, revisa formato de entrada, ratio, duracion y calidad objetivo.',
      headers: {
        setting: 'Ajuste',
        options: 'Opciones',
        bestFor: 'Mejor para',
      },
    },
    promptExamples: {
      eyebrow: 'Prompts',
      title: 'Ejemplos de Grok imagen a video',
      description:
        'Conecta imagen fuente, prompt de movimiento, ajustes y resultado esperado.',
      headers: {
        useCase: 'Caso',
        input: 'Imagen',
        prompt: 'Prompt',
        setting: 'Ajuste',
        result: 'Resultado',
      },
    },
    useCases: {
      eyebrow: 'Casos de uso',
      title: 'Cuando encaja Grok AI imagen a video',
      description:
        'Usalo cuando ya tienes una direccion visual fuerte y necesitas un borrador corto para revisar, probar o publicar.',
    },
    value: {
      eyebrow: 'Valor',
      title: 'Por que usar este generador Grok image to video?',
    },
    limits: {
      eyebrow: 'Limites',
      title: 'Buen input y limites realistas',
      description:
        'Mantén el frame simple, los derechos claros y revisa el clip antes de publicar o vender.',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'FAQ de Grok image to video',
      description:
        'Comprueba entrada, prompts, ajustes y uso seguro antes de generar.',
    },
    cta: {
      eyebrow: 'Listo para animar una imagen?',
      title: 'Sube un frame inicial y genera un borrador Grok image to video.',
      upload: 'Subir imagen',
      model: 'Grok Imagine Video 1.5',
      text: 'Empezar desde texto',
      source: 'Crear imagen fuente',
      history: 'Ver historial',
      generator: 'Generador de imágenes Grok',
      limit: 'FAQ de límite Grok Imagine',
    },
    schema: {
      softwareName: 'Generador Grok image to video',
      howToName: 'Como usar el generador Grok image to video',
      howToDescription:
        'Sube un frame inicial, describe el movimiento, elige ajustes de video, genera y revisa el clip.',
      breadcrumbHome: 'Inicio',
      breadcrumbPage: 'Generador Grok image to video',
    },
  },
};

export function getGrokImageToVideoSeoData(
  locale: Locale = getLocale()
): GrokImageToVideoSeoData {
  if (locale === 'es') return ES_GROK_IMAGE_TO_VIDEO_DATA;
  return EN_GROK_IMAGE_TO_VIDEO_DATA;
}
